import { Button, DatePicker, Input, InputRef, Space } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { rangePresets } from '../pages/billing/transactions/list/consts'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import * as React from 'react'
import { Leaves } from './types'

export const handleReset = <Entity,>(
  clearFilters: () => void,
  dataIndex: Leaves<Entity>,
  setSearchText: React.Dispatch<React.SetStateAction<{ [p: string]: string }>>,
) => {
  clearFilters()
  setSearchText((prev) => {
    delete prev[dataIndex]
    return {
      ...prev,
    }
  })
}

export const getColumnSearchProps = <Entity,>(
  dataIndex: Leaves<Entity>,
  setSearchText: React.Dispatch<React.SetStateAction<{ [p: string]: string }>>,
  searchText: { [p: string]: string },
  searchInput: React.RefObject<InputRef>,
): {
  filterDropdown: ({
    setSelectedKeys,
    selectedKeys,
    clearFilters,
    close,
  }: {
    setSelectedKeys: any
    selectedKeys: any
    clearFilters?: any
    close: any
  }) => JSX.Element
  filterIcon: (filtered: boolean) => JSX.Element
  onFilter: (value: any, record: any) => boolean
  render: (text: any) => any
  onFilterDropdownOpenChange: (visible: any) => void
} => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, clearFilters, close }) => (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <Input
        ref={searchInput}
        placeholder={`Search ${dataIndex}`}
        value={selectedKeys[0]}
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() =>
          setSearchText((prev) => ({
            ...prev,
            [dataIndex]: selectedKeys[0],
          }))
        }
        style={{ marginBottom: 8, display: 'block' }}
      />
      <Space>
        <Button
          type="primary"
          onClick={() => {
            setSearchText((prev) => ({
              ...prev,
              [dataIndex]: selectedKeys[0],
            }))
            close()
          }}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          onClick={() => {
            clearFilters && handleReset(clearFilters, dataIndex, setSearchText)
            close()
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </Space>
    </div>
  ),
  filterIcon: (filtered: boolean) => (
    <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
  ),
  onFilter: (value, record) =>
    record[dataIndex]
      .toString()
      .toLowerCase()
      .includes((value as string).toLowerCase()),
  onFilterDropdownOpenChange: (visible) => {
    if (visible) {
      setTimeout(() => searchInput.current?.select(), 100)
    }
  },
  render: (text) =>
    searchText[dataIndex] ? (
      <Highlighter
        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
        searchWords={[searchText[dataIndex]]}
        autoEscape
        textToHighlight={text ? text.toString() : ''}
      />
    ) : (
      text
    ),
})

export const getColumnSearchDateProps = <Entity,>(
  dataIndex: Leaves<Entity>,
  setSearchText: React.Dispatch<React.SetStateAction<{ [p: string]: string }>>,
  searchText: { [p: string]: string },
  searchInput: React.RefObject<InputRef>,
): {
  filterDropdown: ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
    close,
  }: {
    setSelectedKeys: any
    selectedKeys: any
    confirm: any
    clearFilters?: any
    close: any
  }) => JSX.Element
  filterIcon: (filtered: boolean) => JSX.Element
  onFilter: (value: any, record: any) => boolean
  render: (text: any) => any
  onFilterDropdownOpenChange: (visible: any) => void
} => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, close, clearFilters }) => (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <DatePicker.RangePicker
        format="YYYY-MM-DD"
        style={{ marginBottom: 8, display: 'flex' }}
        value={selectedKeys[0]}
        onChange={(e) => setSelectedKeys(e ? [e] : [])}
        onCalendarChange={(dates) => setSelectedKeys(dates ? [dates] : [])}
        presets={rangePresets}
        allowEmpty={[true, true]}
      />
      <Space>
        <Button
          type="primary"
          onClick={() => {
            setSearchText((prev) => ({
              ...prev,
              [dataIndex]: selectedKeys[0],
            }))
            close()
          }}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          onClick={() => {
            clearFilters && handleReset(clearFilters, dataIndex, setSearchText)
            close()
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </Space>
    </div>
  ),
  filterIcon: (filtered: boolean) => (
    <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    if (!record[dataIndex]) return false
    const date = dayjs(record[dataIndex])
    const [start, end] = value
    if (start && end) {
      dayjs.extend(isBetween)
      return date.isBetween(dayjs(start), dayjs(end), 'day', '[]')
    }
    if (start) {
      dayjs.extend(isSameOrAfter)
      return date.isSameOrAfter(dayjs(start), 'day')
    }
    if (end) {
      dayjs.extend(isSameOrBefore)
      return date.isSameOrBefore(dayjs(end), 'day')
    }
    return true
  },
  onFilterDropdownOpenChange: (visible) => {
    if (visible) {
      setTimeout(() => searchInput.current?.select(), 100)
    }
  },
  render: (text) =>
    searchText[dataIndex] ? (
      <Highlighter
        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
        searchWords={[searchText[dataIndex]]}
        textToHighlight={text ? text.toString() : ''}
      />
    ) : (
      text
    ),
})
